import type { DocContent, Language } from './types';
import { loadDocContent, availableDocSlugs } from './loader';

export interface TagInfo {
  name: string;
  count: number;
  articles: Array<{
    id: string;
    title: string;
    description: string;
    slug: string;
    lastUpdated: string;
  }>;
}

export interface TagCollection {
  all: Map<string, TagInfo>;
  popular: TagInfo[];
  categories: {
    [key: string]: string[];
  };
}

export class TagManager {
  private tagCollections: Map<Language, TagCollection> = new Map();
  private initialized = false;

  async initialize() {
    if (this.initialized) return;
    
    // Build tag collections for all languages
    const languages: Language[] = ['en', 'sv-SE', 'fr-FR', 'es-ES', 'de-DE', 'ja-JP', 'zh-CN', 'it-IT', 'ko-KR', 'pt-BR', 'vi-VN', 'pl-PL', 'nl-NL', 'nb-NO', 'da-DK', 'fi-FI', 'uk-UA'];
    
    for (const lang of languages) {
      await this.buildTagCollection(lang);
    }
    
    this.initialized = true;
  }

  private async buildTagCollection(language: Language) {
    const collection: TagCollection = {
      all: new Map(),
      popular: [],
      categories: {
        'Getting Started': ['introduction', 'overview', 'getting-started', 'installation', 'quick-start', 'setup'],
        'Features': ['projects', 'tasks', 'time-tracking', 'teams', 'collaboration', 'workspaces', 'organization', 'calendar', 'reports'],
        'Developer': ['api', 'cli', 'webhooks', 'integrations', 'command-line', 'terminal', 'developers'],
        'Business': ['billing', 'invoicing', 'clients', 'customer-management', 'enterprise'],
        'Security': ['authentication', 'security', 'privacy', 'permissions', 'sso'],
        'Support': ['troubleshooting', 'faq', 'contact', 'support', 'help']
      }
    };

    // Get all documentation articles
    const allSlugs = await this.getAllDocSlugs();
    
    for (const slug of allSlugs) {
      try {
        const content = await loadDocContent(slug, language);
        if (!content || !content.tags) continue;

        // Process each tag
        for (const tag of content.tags) {
          const normalizedTag = tag.toLowerCase().trim();
          
          if (!collection.all.has(normalizedTag)) {
            collection.all.set(normalizedTag, {
              name: normalizedTag,
              count: 0,
              articles: []
            });
          }

          const tagInfo = collection.all.get(normalizedTag)!;
          tagInfo.count++;
          tagInfo.articles.push({
            id: content.id,
            title: content.title,
            description: content.description,
            slug: slug,
            lastUpdated: content.lastUpdated
          });
        }
      } catch (error) {
        console.error(`Error processing doc ${slug}:`, error);
      }
    }

    // Sort articles within each tag by last updated
    for (const tagInfo of collection.all.values()) {
      tagInfo.articles.sort((a, b) => 
        new Date(b.lastUpdated).getTime() - new Date(a.lastUpdated).getTime()
      );
    }

    // Determine popular tags (top 10 by count)
    collection.popular = Array.from(collection.all.values())
      .sort((a, b) => b.count - a.count)
      .slice(0, 10);

    this.tagCollections.set(language, collection);
  }

  private async getAllDocSlugs(): Promise<string[]> {
    // Source slugs from the real docs on disk (loader derives this from the
    // markdown files via import.meta.glob). Previously this was a hardcoded
    // list of mostly-nonexistent slugs, which made Related Articles and
    // Popular Tags compute against phantom files.
    return [...availableDocSlugs];
  }

  async getTagCollection(language: Language = 'en'): Promise<TagCollection | undefined> {
    if (!this.initialized) {
      await this.initialize();
    }
    return this.tagCollections.get(language);
  }

  async getTagInfo(tagName: string, language: Language = 'en'): Promise<TagInfo | undefined> {
    const collection = await this.getTagCollection(language);
    if (!collection) return undefined;
    
    const normalizedTag = tagName.toLowerCase().trim();
    return collection.all.get(normalizedTag);
  }

  async getPopularTags(language: Language = 'en', limit: number = 10): Promise<TagInfo[]> {
    const collection = await this.getTagCollection(language);
    if (!collection) return [];
    
    return collection.popular.slice(0, limit);
  }

  async getRelatedTags(tagName: string, language: Language = 'en'): Promise<string[]> {
    const collection = await this.getTagCollection(language);
    if (!collection) return [];
    
    const normalizedTag = tagName.toLowerCase().trim();
    const relatedTags = new Set<string>();
    
    // Find which category this tag belongs to
    for (const [category, tags] of Object.entries(collection.categories)) {
      if (tags.includes(normalizedTag)) {
        // Add all tags from the same category
        tags.forEach(t => {
          if (t !== normalizedTag && collection.all.has(t)) {
            relatedTags.add(t);
          }
        });
      }
    }
    
    // Also find tags that frequently appear together
    const tagInfo = collection.all.get(normalizedTag);
    if (tagInfo) {
      for (const article of tagInfo.articles) {
        // Get the full article content to access all its tags
        try {
          const content = await loadDocContent(article.slug, language);
          if (content && content.tags) {
            content.tags.forEach(t => {
              const normalized = t.toLowerCase().trim();
              if (normalized !== normalizedTag && collection.all.has(normalized)) {
                relatedTags.add(normalized);
              }
            });
          }
        } catch (error) {
          console.error(`Error getting related tags for ${article.slug}:`, error);
        }
      }
    }
    
    return Array.from(relatedTags);
  }

  async searchByTag(tagName: string, language: Language = 'en'): Promise<DocContent[]> {
    const tagInfo = await this.getTagInfo(tagName, language);
    if (!tagInfo) return [];
    
    const results: DocContent[] = [];
    for (const article of tagInfo.articles) {
      try {
        const content = await getDocumentContent(article.slug, language);
        if (content) {
          results.push(content);
        }
      } catch (error) {
        console.error(`Error loading article ${article.slug}:`, error);
      }
    }
    
    return results;
  }
}

// Singleton instance
export const tagManager = new TagManager();